import { Formik, FormikHelpers } from 'formik';
import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import { withQueryParams } from '@shared/commonFunctions';
import { SaveButton } from '@shared/components/SaveButton';

import { FormWithPrompt } from '@shared/components/FormWithPrompt';
import { CreditBureauScoreTypeMap, labelToMapKey } from '@modules/ContactManager/components/Header/interfaces';
import { TAssetType } from '../../interfaces';
import { requestUpdateAssets } from '../Assets/queries';
import { ILenderUtilityTabProps } from '../LenderUtilityTabs';
import { requestAddLender, requestUpdateLoanInfo } from '../requests';
import { LoanInfoForm } from './LoanInfoForm';
import { lenderUtilityLoanInfoFormSchema, TLenderUtilityLoanInfoForm } from './validation';

const defaultAt: TAssetType[] = [
  { assetType: 'Car', isAllowed: true },
  { assetType: 'Truck', isAllowed: true },
  { assetType: 'Suv', isAllowed: true },
];

const DEFAULT_SCORE_TYPE = 'Tu';

export const LoanInfo = ({ data, create, mutate }: ILenderUtilityTabProps) => {
  const navigate = useNavigate();

  const handleSubmit = useCallback(
    async (values: TLenderUtilityLoanInfoForm, formikActions: FormikHelpers<TLenderUtilityLoanInfoForm>) => {
      const formattedData = {
        ...values,
        generalInfo: {
          ...values.generalInfo,
          creditReport: values.generalInfo.creditReport || null,
          defaultScore: labelToMapKey[values.generalInfo.defaultScore],
          acceptedChannels: values.generalInfo.acceptedChannels || [],
          ...(typeof values.generalInfo.availableMarkupIncrements === 'number' && {
            availableMarkupIncrements: String(values.generalInfo.availableMarkupIncrements),
          }),
        },
        productCheckRequestPayee: {
          ...values.productCheckRequestPayee,
          isPayOffAddressForGAP:
            values.productCheckRequestPayee?.isPayOffAddressForGAP ?? false,
        },
      };

      if (create) {
        const newLender = await requestAddLender(formattedData);

        if (newLender) {
          navigate(withQueryParams('.', { lenderId: newLender.id }));
          await requestUpdateAssets(newLender.id, defaultAt);
        }
      } else {
        // formattedData.generalInfo.secondaryBureau = !formattedData.generalInfo.secondaryBureau
        //   ? null
        //   : formattedData.generalInfo.secondaryBureau;
        const resp = await requestUpdateLoanInfo(formattedData);
        if (resp?.status === 204) {
          mutate?.();
        }
      }

      formikActions.setSubmitting(false);
    },
    [create, requestAddLender, requestUpdateLoanInfo]
  );

const initialState = {
    ...data,
    id: data?.id,
    name: data?.name,
    nameOnDocs: data?.nameOnDocs ?? data?.name,
    code: data?.code,
    contactPhone: data?.contactPhone,
    rank: data?.rank,
    subRank: data?.subRank,
    ltvType: data?.ltvType,
    submissionMethod: data?.isDealertrack
      ? 'isDealertrack'
      : data?.isOpenLending
      ? 'isOpenLending'
      : data?.isMeridian
      ? 'isMeridian'
      : data?.isQuilo
      ? 'isQuilo'
      : data?.isCudl
      ? 'isCudl'
      : data?.isSubprime
      ? 'isSubprime'
      : data?.isOrigence
      ? 'isOrigence'
      : undefined,
    isDealertrack: data?.isDealertrack ?? false,
    isQuilo: data?.isQuilo ?? false,
    isOpenLending: data?.isOpenLending ?? false,
    isMeridian: data?.isMeridian ?? false,
    isCudl: data?.isCudl ?? false,
    isOrigence: data?.isOrigence ?? false,
    isSubprime: data?.isSubprime ?? false,
    useBothLtv: data?.useBothLtv,
    isFlStampFee: data?.isFlStampFee,
    acceptsMla: data?.acceptsMla ?? false,
    cuCollaborateCDFICheck: data?.cuCollaborateCDFICheck,
    isCUCollaborateOnly: data?.isCUCollaborateOnly,
    dealertrackId: data?.dealertrackId,
    openLendingConfiguration: {
      userName: data?.openLendingConfiguration?.userName || null,
      password: data?.openLendingConfiguration?.password || null,
      institutionId: data?.openLendingConfiguration?.institutionId || null,
      endpointUrl: data?.openLendingConfiguration?.endpointUrl || null,
    },
    meridianConfiguration: {
      userId: data?.meridianConfiguration?.userId || null,
      meridianDecisionLoanUrl: data?.meridianConfiguration?.meridianDecisionLoanUrl || null,
      password: data?.meridianConfiguration?.password || null,
      meridianSubmitDocUrl: data?.meridianConfiguration?.meridianSubmitDocUrl || null,
    },
    cudlConfiguration: {
      code: data?.cudlConfiguration?.code || null,
      userId: data?.cudlConfiguration?.userId || null,
      password: data?.cudlConfiguration?.password || null,
      organizationCode: data?.cudlConfiguration?.organizationCode || null,
      url: data?.cudlConfiguration?.url || null,
      decisionAuthKey: data?.cudlConfiguration?.decisionAuthKey || null,
    },
    meridianLenderReferenceId: data?.meridianLenderReferenceId,
    meridianOrgReferenceId: data?.meridianOrgReferenceId,
    generalInfo: {
      fundingFeeGreen: data?.generalInfo?.fundingFeeGreen,
      acceptedChannels: data?.generalInfo?.acceptedChannels || [],
      maxFlatGreen: data?.generalInfo?.maxFlatGreen,
      maxFlatPercentGreen: data?.generalInfo?.maxFlatPercentGreen,
      allowEmployerZip: data?.generalInfo?.allowEmployerZip,
      defaultValuationMethods: data?.generalInfo?.defaultValuationMethods || [],
      acceptedResidency: data?.generalInfo?.acceptedResidency || [],
      acceptedResidencyPairs: data?.generalInfo?.acceptedResidencyPairs || [],
      prohibitedLienHolders: data?.generalInfo?.prohibitedLienHolders || [],
      daysToFirstPaymentGreen: data?.generalInfo?.daysToFirstPaymentGreen,
      maxDtiGreen: data?.generalInfo?.maxDtiGreen,
      maxPtiGreen: data?.generalInfo?.maxPtiGreen,
      maxMileageGreen: data?.generalInfo?.maxMileageGreen,
      maxAgeGreen: data?.generalInfo?.maxAgeGreen,
      maxCashOutGreen: data?.generalInfo?.maxCashOutGreen,
      minPrimaryFicoGreen: data?.generalInfo?.minPrimaryFicoGreen,
      minSecondaryFicoGreen: data?.generalInfo?.minSecondaryFicoGreen,
      availableMarkupIncrements: data?.generalInfo?.availableMarkupIncrements,
      achDiscount: data?.generalInfo?.achDiscount,
      gapLtvLimit: data?.generalInfo?.gapLtvLimit,
      maxProduct: data?.generalInfo?.maxProduct,
      maxOtherProducts: data?.generalInfo?.maxOtherProducts,
      defaultScore:
        CreditBureauScoreTypeMap[
          (data?.generalInfo?.defaultScore as keyof typeof CreditBureauScoreTypeMap) ?? DEFAULT_SCORE_TYPE
        ],
      preferredScore: data?.generalInfo?.preferredScore,
      creditReport: data?.generalInfo?.creditReport || null,
      approvalDaysGreen: data?.generalInfo?.approvalDaysGreen,
      daysToFirstPaymentYellow: data?.generalInfo?.daysToFirstPaymentYellow,
      maxDtiYellow: data?.generalInfo?.maxDtiYellow,
      maxPtiYellow: data?.generalInfo?.maxPtiYellow,
      maxMileageYellow: data?.generalInfo?.maxMileageYellow,
      maxAgeYellow: data?.generalInfo?.maxAgeYellow,
      minPrimaryFicoYellow: data?.generalInfo?.minPrimaryFicoYellow,
      minSecondaryFicoYellow: data?.generalInfo?.minSecondaryFicoYellow,
      approvalDaysYellow: data?.generalInfo?.approvalDaysYellow,
      maxCashOutYellow: data?.generalInfo?.maxCashOutYellow,
      fundingFeeYellow: data?.generalInfo?.fundingFeeYellow,
      requirements: data?.generalInfo?.requirements,
      useAprBasedCalculation: data?.generalInfo?.useAprBasedCalculation,
    },
  };

  return (
    <Formik
      enableReinitialize
      initialValues={initialState as TLenderUtilityLoanInfoForm}
      validationSchema={lenderUtilityLoanInfoFormSchema}
      onSubmit={handleSubmit}
    >
      {(props) => (
        <FormWithPrompt>
          <SaveButton disabled={!props.isValid && !!props.submitCount} />
          <LoanInfoForm
            create={create}
            values={props.values}
            setFieldValue={props.setFieldValue} 
          />
        </FormWithPrompt>
      )}
    </Formik>
  );
};
